(() => {
  if (customElements.get('mamami-breakfast-builder')) return;
  const validMoney = value => Number.isSafeInteger(value) && value >= 0;
  const modulo = (value, length) => ((value % length) + length) % length;
  const quantityRule = entry => {
    const rule = entry?.quantityRule;
    const increment = Number.isSafeInteger(rule?.increment) && rule.increment > 0 ? rule.increment : 1;
    const minimum = Number.isSafeInteger(rule?.min) && rule.min > 0 ? rule.min : 1;
    return { min: Math.ceil(minimum / increment) * increment, increment, max: Number.isSafeInteger(rule?.max) && rule.max > 0 ? rule.max : null };
  };
  const validQuantity = (entry, value) => {
    const rule = quantityRule(entry);
    return !!entry && Number.isSafeInteger(value) && value >= rule.min && value % rule.increment === 0 && (rule.max === null || value <= rule.max) && (!validMoney(entry.price) || validMoney(entry.price * value));
  };
  class BreakfastBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.strings = JSON.parse(this.querySelector('[data-breakfast-strings]').textContent);
      this.entries = [...this.querySelectorAll('[data-breakfast-entry]')].map(el => JSON.parse(el.textContent));
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'MYR', currencyDisplay: 'narrowSymbol' });
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.state = { oats: null, drink: null, request: 'idle' };
      this.composition = new Map();
      this.wheels = {};
      this.storageKey = 'mamami-breakfast-cart-v1';
      this.store = new window.MamamiBreakfastStorage(window.Shopify?.shop || location.hostname, window.Shopify?.theme);
      this.commerce = { busy: false, verified: false, message: '' };
      this.commerce.storageFailed = !this.store.available || !navigator.locks?.request;
      if (this.commerce.storageFailed) this.persistenceNotice = this.strings.storageUnavailable;
      this.readPending();
      for (const group of ['oats', 'drink']) {
        const root = this.querySelector(`[data-wheel="${group}"]`);
        const seen = new Set();
        const entries = this.entries.filter(e => e.group === group).filter(e => {
          const identity = e.variantId || e.requestedVariantId || e.key;
          if (seen.has(identity)) return false;
          seen.add(identity); return true;
        });
        const starting = entries.findIndex(e => e.key === root.dataset.start);
        const wheel = { group, root, orbit: root.querySelector('[data-orbit]'), packages: root.querySelector('[data-packages]'), entries, index: Math.max(0, starting), busy: false, token: 0, animations: [] };
        this.wheels[group] = wheel;
        this.state[group] = entries[wheel.index] || null;
        this.bindWheel(wheel);
        this.renderWheel(wheel);
        this.renderDetails(group);
      }
      this.defaults = ['oats', 'drink'].map(group => this.state[group]).filter(entry => entry?.variantId);
      this.addEventListener('click', event => {
        if (event.target.closest('[data-purchase]')) { this.purchase(event.target.closest('[data-purchase]')); return; }
        if (event.target.closest('[data-cart-recheck]')) { this.recheck(); return; }
        if (event.target.closest('[data-start-over]')) { this.startOver(); return; }
        const add = event.target.closest('[data-add-breakfast]');
        if (add) { this.addToBreakfast(add.closest('[data-wheel]').dataset.wheel); return; }
        const row = event.target.closest('[data-composition-id]');
        if (!row) return;
        if (event.target.closest('[data-remove-line]')) { this.removeLine(row.dataset.compositionId); return; }
        const step = event.target.closest('[data-quantity-step]');
        if (step) {
          const line = this.composition.get(row.dataset.compositionId);
          this.setQuantity(row.dataset.compositionId, line.quantity + Number(step.dataset.quantityStep) * quantityRule(line.entry).increment);
        }
      }, { signal: this.signal });
      for (const type of ['input', 'change']) {
        this.addEventListener(type, event => {
          if (!event.target.matches('[data-quantity]')) return;
          this.setQuantity(event.target.closest('[data-composition-id]').dataset.compositionId, Number(event.target.value), type === 'change');
        }, { signal: this.signal });
      }
      this.addEventListener('submit', event => event.preventDefault(), { signal: this.signal });
      window.addEventListener('storage', event => {
        if (event.key !== this.store.journalKey) return;
        this.commerce.confirmedQuote = null;
        if (!this.commerce.busy) { this.readPending(); this.commerce.verified = false; this.renderSummary(); }
      }, { signal: this.signal });
      window.addEventListener('pageshow', event => { if (event.persisted) this.refreshAfterReturn(); }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.refreshAfterReturn(); }, { signal: this.signal });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
        if (event.source === 'breakfast-builder') return;
        this.commerce.confirmedQuote = null; this.renderSummary();
      });
      this.mobile.addEventListener('change', () => {
        for (const wheel of Object.values(this.wheels)) {
          wheel.token++; wheel.animations.forEach(a => a.cancel()); wheel.busy = false; wheel.drag = 0; wheel.pointer = null;
          wheel.root.removeAttribute('aria-busy');
          this.renderWheel(wheel);
          this.reserveGallery(wheel.group);
        }
        this.renderSummary();
      }, { signal: this.signal });
      this.dataset.enhanced = 'true';
      if (!this.restoreDraft() && !this.commerce.pending) this.seedDefaults();
      this.renderSummary();
      if (this.commerce.pending) this.restorePending();
      else this.verifySelection();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.cartUnsubscribe?.();
      clearTimeout(this.verificationTimer); this.verificationToken = (this.verificationToken || 0) + 1;
      for (const wheel of Object.values(this.wheels || {})) { wheel.token++; wheel.animations.forEach(a => a.cancel()); }
      this.controller = null;
    }
    format(value) { return validMoney(value) ? this.money.format(value / 100) : this.strings.priceUnavailable; }
    total(replacementId, replacementQuantity, pricedOnly = false) {
      let sum = 0;
      for (const [id, { entry, quantity }] of this.composition) {
        const value = id === replacementId ? replacementQuantity : quantity;
        if (pricedOnly && !validMoney(entry.price)) continue;
        if (!validMoney(entry.price) || !validQuantity(entry, value)) return null;
        sum += entry.price * value;
        if (!validMoney(sum)) return null;
      }
      return sum;
    }
    locked() { return this.commerce.busy || !!this.commerce.pending || !!this.commerce.journalCorrupt; }
    unavailableEntry(id) {
      return { key: id, variantId: id, requestedVariantId: id, productId: '', productHandle: '', name: this.strings.removedProduct, pack: '', price: null, available: false, productImage: null, removed: true };
    }
    seedDefaults() {
      this.composition.clear();
      for (const entry of this.defaults) if (!this.composition.has(entry.variantId)) this.composition.set(entry.variantId, { entry, quantity: quantityRule(entry).min });
      this.saveDraft();
    }
    startOver() {
      if (this.locked()) return;
      this.seedDefaults(); this.persistenceNotice = this.strings.resetBreakfast;
      this.compositionChanged(this.strings.resetBreakfast);
      this.querySelector('[data-composition-heading]').focus({ preventScroll: true });
    }
    restoreDraft() {
      const draft = this.store.readPreferences();
      if (draft.kind === 'new') return false;
      for (const saved of draft.items) {
        const configured = this.entries.find(entry => (entry.variantId || entry.requestedVariantId) === saved.id);
        const entry = configured?.variantId ? configured : this.unavailableEntry(saved.id);
        this.composition.set(saved.id, { entry, quantity: saved.quantity });
      }
      this.persistenceNotice = this.strings[draft.kind === 'restored' ? 'restoredBreakfast' : draft.kind === 'expired' ? 'expiredBreakfast' : draft.kind === 'corrupt' ? 'corruptBreakfast' : 'storageUnavailable'];
      if (['expired', 'corrupt'].includes(draft.kind)) this.saveDraft();
      return true;
    }
    saveDraft() {
      if (!this.store.savePreferences([...this.composition].map(([id, line]) => ({ id, quantity: line.quantity })))) {
        this.commerce.storageFailed = true; this.persistenceNotice = this.strings.storageUnavailable;
      }
    }
    refreshAfterReturn() {
      if (this.commerce.busy || !this.isConnected) return;
      this.readPending(); this.commerce.confirmedQuote = null; this.commerce.verified = false;
      if (this.commerce.pending) this.restorePending(); else this.verifySelection();
    }
    compositionChanged(notice) {
      this.commerce.verified = false; this.commerce.message = '';
      this.commerce.confirmedQuote = null;
      if (!this.commerce.storageFailed) this.persistenceNotice = notice || this.strings.savedBreakfast;
      this.verificationToken = (this.verificationToken || 0) + 1;
      clearTimeout(this.verificationTimer);
      this.saveDraft(); this.renderSummary();
      this.verificationTimer = setTimeout(() => this.verifySelection(), 150);
    }
    addToBreakfast(group) {
      const wheel = this.wheels[group], entry = this.state[group];
      if (this.locked() || wheel.busy || wheel.drag || !entry?.available || !entry.variantId) return;
      const line = this.composition.get(entry.variantId);
      if (line) {
        if (!validQuantity(line.entry, line.quantity + 1)) return;
        this.setQuantity(entry.variantId, line.quantity + 1);
      } else {
        const quantity = quantityRule(entry).min;
        if (!validQuantity(entry, quantity)) return;
        this.composition.set(entry.variantId, { entry, quantity });
        if (this.total(undefined, undefined, true) === null) { this.composition.delete(entry.variantId); return; }
        this.compositionChanged();
      }
      this.querySelector('[data-composition-announcement]').textContent = `${entry.name}, ${entry.pack}. ${this.strings.addedBreakfast}. ${this.strings.quantity}: ${this.composition.get(entry.variantId).quantity}.`;
    }
    removeLine(id) {
      if (this.locked()) return;
      const line = this.composition.get(id); if (!line) return;
      const ids = [...this.composition.keys()], index = ids.indexOf(id);
      const node = this.querySelector(`[data-composition-id="${CSS.escape(id)}"]`);
      const restoreFocus = node?.contains(document.activeElement);
      this.composition.delete(id); this.compositionChanged();
      if (restoreFocus) {
        const nextId = [...this.composition.keys()][Math.min(index, this.composition.size - 1)];
        (this.querySelector(`[data-composition-id="${CSS.escape(nextId || '')}"] [data-remove-line]`) || this.querySelector('[data-composition-heading]')).focus({ preventScroll: true });
      }
      this.querySelector('[data-composition-announcement]').textContent = `${line.entry.name}, ${line.entry.pack}. ${this.strings.removedBreakfast}.`;
    }
    setQuantity(id, value, restoreInvalid = true) {
      const line = this.composition.get(id); if (!line || this.locked()) return;
      const input = this.querySelector(`[data-composition-id="${CSS.escape(id)}"] [data-quantity]`);
      if (!validQuantity(line.entry, value) || this.total(id, value, true) === null) {
        if (restoreInvalid) { input.value = line.quantity; input.removeAttribute('aria-invalid'); }
        else input.setAttribute('aria-invalid', 'true');
        this.renderSummary(); return;
      }
      input.removeAttribute('aria-invalid');
      if (line.quantity !== value) { line.quantity = value; this.compositionChanged(); }
      else this.renderSummary();
    }
    renderComposition() {
      const list = this.querySelector('[data-composition-lines]'), locked = this.locked();
      for (const node of [...list.children]) if (!this.composition.has(node.dataset.compositionId)) node.remove();
      for (const [id, { entry, quantity }] of this.composition) {
        let node = [...list.children].find(node => node.dataset.compositionId === id);
        if (!node) {
          node = this.querySelector('[data-composition-template]').content.firstElementChild.cloneNode(true);
          node.dataset.compositionId = id; list.append(node);
          node.querySelector('[data-line-name]').id = `BreakfastLine-${this.dataset.sectionId}-${id}`;
          node.querySelector('[data-quantity]').setAttribute('aria-labelledby', `BreakfastQuantity-${this.dataset.sectionId}-${id} BreakfastLine-${this.dataset.sectionId}-${id}`);
          node.querySelector('[data-line-quantity-label]').id = `BreakfastQuantity-${this.dataset.sectionId}-${id}`;
        }
        const image = node.querySelector('[data-line-image]'), picture = entry.productImage;
        image.hidden = !picture || !this.safeImage(picture);
        if (!image.hidden && image.getAttribute('src') !== picture) image.src = picture;
        node.querySelector('[data-line-name]').textContent = entry.name;
        node.querySelector('[data-line-pack]').textContent = [entry.pack, entry.variantTitle && entry.variantTitle !== 'Default Title' ? entry.variantTitle : ''].filter(Boolean).join(' · ');
        node.querySelector('[data-line-unit]').textContent = this.format(entry.price);
        node.querySelector('[data-line-subtotal]').textContent = this.format(validMoney(entry.price) && validQuantity(entry, quantity) ? entry.price * quantity : null);
        node.querySelector('[data-line-availability]').textContent = !entry.variantId || !entry.available ? this.strings.unavailable : !validQuantity(entry, quantity) ? this.strings.quantityInvalid : '';
        const rule = quantityRule(entry), input = node.querySelector('[data-quantity]');
        if (input.getAttribute('aria-invalid') !== 'true') input.value = quantity;
        input.min = rule.min; input.step = rule.increment; input.disabled = locked;
        if (rule.max === null) input.removeAttribute('max'); else input.max = rule.max;
        for (const direction of [-1, 1]) {
          const button = node.querySelector(`[data-quantity-step="${direction}"]`);
          button.disabled = locked || !validQuantity(entry, quantity + direction * rule.increment);
          button.setAttribute('aria-label', `${direction < 0 ? this.strings.decrease : this.strings.increase}: ${entry.name}, ${entry.pack}`);
        }
        const remove = node.querySelector('[data-remove-line]'); remove.disabled = locked;
        remove.setAttribute('aria-label', `${this.strings.remove}: ${entry.name}, ${entry.pack}`);
      }
      this.querySelector('[data-composition-empty]').hidden = this.composition.size > 0;
    }
    bindWheel(wheel) {
      const { root, orbit } = wheel;
      const touchSurface = wheel.packages || orbit;
      const useTouchEvents = 'ontouchstart' in window || typeof window.PointerEvent !== 'function';
      if (useTouchEvents) touchSurface.style.touchAction = 'pan-y pinch-zoom';
      if (!orbit.hasAttribute('tabindex')) orbit.tabIndex = 0;
      orbit.setAttribute('aria-keyshortcuts', 'ArrowLeft ArrowRight');
      orbit.setAttribute('data-mbb-input', useTouchEvents ? 'touch' : 'pointer');
      orbit.setAttribute('data-mbb-gesture-version', 'native-touch-20261026');
      root.addEventListener('click', event => {
        if (this.commerce.busy || this.commerce.pending) return;
        if (event.target.closest('[data-orbit]') && performance.now() < (wheel.suppressClickUntil || 0)) return;
        const direction = event.target.closest('[data-direction]');
        const tile = event.target.closest('[data-entry-key]');
        if (direction) this.rotate(wheel, Number(direction.dataset.direction));
        else if (tile && tile.dataset.position !== 'center') this.rotate(wheel, tile.dataset.position === 'previous' ? -1 : 1);
      }, { signal: this.signal });
      root.addEventListener('keydown', event => {
        if (event.target.closest('input, [data-quantity-controls]')) return;
        const keys = ['ArrowLeft', 'ArrowRight'];
        if (!keys.includes(event.key) || wheel.entries.length < 2) return;
        event.preventDefault(); this.rotate(wheel, event.key === keys[0] ? -1 : 1);
      }, { signal: this.signal });
      const start = (id, x, y, target, pointer = false) => {
        if (this.commerce.busy || this.commerce.pending || target.closest('.mbb__caption') || wheel.busy || wheel.pointer || wheel.entries.length < 2) return;
        wheel.drag = 0;
        wheel.pointer = { id, x, y, axis: null, distance: this.wheelDistance(wheel), pointer };
      };
      const move = (id, x, y, event) => {
        const p = wheel.pointer; if (!p || p.id !== id) return;
        const dx = x - p.x, dy = y - p.y;
        if (!p.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 6) {
          if (Math.abs(dx) > Math.abs(dy) * 1.1) p.axis = 'wheel';
          else if (Math.abs(dy) > Math.abs(dx) * 1.1) p.axis = 'page';
          else return;
          if (p.axis === 'wheel') {
            if (p.pointer) { try { orbit.setPointerCapture(id); } catch {} }
            wheel.drag = .001; this.renderSummary();
          }
        }
        if (p.axis !== 'wheel') return;
        if (event.cancelable) event.preventDefault();
        wheel.drag = Math.max(-1, Math.min(1, dx / p.distance));
        if (!this.reduced.matches) this.paintDrag(wheel, wheel.drag);
      };
      const finish = (id, x, cancel = false) => {
        const p = wheel.pointer; if (!p || p.id !== id) return;
        wheel.pointer = null;
        if (p.pointer) { try { if (orbit.hasPointerCapture(id)) orbit.releasePointerCapture(id); } catch {} }
        if (p.axis !== 'wheel') return;
        wheel.suppressClickUntil = performance.now() + 450;
        const delta = x - p.x;
        const threshold = Math.max(24, Math.min(36, p.distance * .22));
        if (!cancel && Math.abs(delta) >= threshold && !this.commerce.busy && !this.commerce.pending) this.rotate(wheel, delta < 0 ? 1 : -1);
        else this.settleWheel(wheel);
      };
      if (typeof window.PointerEvent === 'function') {
        orbit.addEventListener('pointerdown', event => {
          if ((useTouchEvents && event.pointerType === 'touch') || event.isPrimary === false || (event.pointerType !== 'touch' && event.button !== 0)) return;
          start(event.pointerId, event.clientX, event.clientY, event.target, true);
        }, { signal: this.signal });
        orbit.addEventListener('pointermove', event => { if (wheel.pointer?.pointer) move(event.pointerId, event.clientX, event.clientY, event); }, { signal: this.signal, passive: false });
        orbit.addEventListener('pointerup', event => { if (wheel.pointer?.pointer) finish(event.pointerId, event.clientX); }, { signal: this.signal });
        orbit.addEventListener('pointercancel', event => { if (wheel.pointer?.pointer) finish(event.pointerId, event.clientX, true); }, { signal: this.signal });
        orbit.addEventListener('lostpointercapture', event => { if (wheel.pointer?.pointer) finish(event.pointerId, event.clientX, true); }, { signal: this.signal });
      }
      if (useTouchEvents) {
        touchSurface.addEventListener('touchstart', event => {
          if (event.touches.length !== 1) {
            if (wheel.pointer) finish(wheel.pointer.id, wheel.pointer.x, true);
            return;
          }
          const touch = event.touches[0];
          start(touch.identifier, touch.clientX, touch.clientY, event.target);
        }, { signal: this.signal, passive: true, capture: true });
        touchSurface.addEventListener('touchmove', event => {
          const p = wheel.pointer; if (!p) return;
          if (event.touches.length !== 1) { finish(p.id, p.x, true); return; }
          const touch = Array.from(event.touches).find(item => item.identifier === p.id);
          if (touch) move(touch.identifier, touch.clientX, touch.clientY, event);
        }, { signal: this.signal, passive: false, capture: true });
        touchSurface.addEventListener('touchend', event => {
          const p = wheel.pointer; if (!p) return;
          const touch = Array.from(event.changedTouches).find(item => item.identifier === p.id);
          if (touch) finish(touch.identifier, touch.clientX);
        }, { signal: this.signal, capture: true });
        touchSurface.addEventListener('touchcancel', () => {
          if (wheel.pointer) finish(wheel.pointer.id, wheel.pointer.x, true);
        }, { signal: this.signal, capture: true });
      }
    }
    wheelDistance(wheel) {
      const value = Number.parseFloat(getComputedStyle(wheel.root).getPropertyValue('--mbb-mobile-distance'));
      return Number.isFinite(value) && value > 0 ? value : 130;
    }
    async settleWheel(wheel) {
      if (wheel.busy || !this.isConnected) return;
      wheel.busy = true; const token = ++wheel.token;
      wheel.root.setAttribute('aria-busy', 'true'); this.renderSummary();
      if (!this.reduced.matches && wheel.drag) {
        wheel.animations = [...wheel.orbit.querySelectorAll('[data-entry-key]')].map(tile => {
          const pose = this.pose(Number(tile.dataset.offset), wheel);
          return tile.animate([{ transform: tile.style.transform || pose }, { transform: pose }], { duration: 180, easing: 'cubic-bezier(.22,.7,.25,1)', fill: 'forwards' });
        });
        await Promise.all(wheel.animations.map(a => a.finished.catch(() => {})));
      } else await Promise.resolve();
      if (token !== wheel.token || !this.isConnected) return;
      wheel.drag = 0; wheel.busy = false; wheel.animations.forEach(a => a.cancel());
      this.renderWheel(wheel); this.renderSummary(); wheel.root.removeAttribute('aria-busy');
    }
    pose(value, wheel) {
      const styles = getComputedStyle(wheel.root);
      const neighborScale = Number.parseFloat(styles.getPropertyValue('--mbb-neighbor-scale'));
      const scale = 1 - Math.min(Math.abs(value), 2) * (1 - neighborScale);
      const distance = this.wheelDistance(wheel);
      return `translate(calc(-50% + ${value * distance}px), calc(-50% + ${Math.abs(value) * 18}px)) scale(${scale})`;
    }
    paintDrag(wheel, amount) {
      for (const tile of wheel.orbit.querySelectorAll('[data-entry-key]')) {
        const pos = Number(tile.dataset.offset) + amount;
        tile.style.transform = this.pose(pos, wheel);
      }
    }
    async rotate(wheel, direction) {
      if (this.commerce.busy || this.commerce.pending || wheel.entries.length < 2 || wheel.busy || !this.isConnected) return;
      if (wheel.entries.length === 2) direction = 1;
      wheel.busy = true; const token = ++wheel.token;
      this.renderSummary();
      wheel.root.setAttribute('aria-busy', 'true');
      const oldFocus = document.activeElement;
      const focusWasTile = oldFocus?.matches('[data-entry-key]') && wheel.orbit.contains(oldFocus);
      const reduced = this.reduced.matches;
      const duration = wheel.drag ? Math.max(120, Math.round(340 * (1 - Math.min(Math.abs(wheel.drag), 1)))) : 340;
      if (!reduced) {
        wheel.animations = [...wheel.orbit.querySelectorAll('[data-entry-key]')].map(tile => {
          const offset = Number(tile.dataset.offset);
          const target = offset - direction;
          return tile.animate([{ transform: tile.style.transform || this.pose(offset, wheel), opacity: 1 }, { transform: this.pose(target, wheel), opacity: Math.abs(target) > 1 ? 0 : 1 }], { duration, easing: 'cubic-bezier(.22,.7,.25,1)', fill: 'forwards' });
        });
        await Promise.all(wheel.animations.map(a => a.finished.catch(() => {})));
      } else await Promise.resolve();
      if (token !== wheel.token || !this.isConnected) return;
      wheel.index = modulo(wheel.index + direction, wheel.entries.length);
      this.state[wheel.group] = wheel.entries[wheel.index];
      wheel.drag = 0; wheel.busy = false; wheel.animations.forEach(a => a.cancel());
      this.renderWheel(wheel); this.renderDetails(wheel.group); this.renderSummary();
      wheel.root.removeAttribute('aria-busy');
      const entry = this.state[wheel.group];
      wheel.root.querySelector('[data-wheel-announcement]').textContent = `${entry.name}, ${entry.pack}. ${this.strings.selected}. ${wheel.index + 1} ${this.strings.of} ${wheel.entries.length}.`;
      if (focusWasTile) wheel.orbit.focus({ preventScroll: true });
    }
    renderWheel(wheel) {
      const entry = this.state[wheel.group], count = wheel.entries.length;
      const positions = count > 2 ? [-1, 0, 1] : count === 2 ? [0, 1] : count === 1 ? [0] : [];
      const fragment = document.createDocumentFragment();
      for (const offset of positions) {
        const item = wheel.entries[modulo(wheel.index + offset, count)];
        const tile = document.createElement(offset === 0 ? 'div' : 'button'); if (offset !== 0) tile.type = 'button'; tile.className = 'mbb__package';
        tile.dataset.position = offset < 0 ? 'previous' : offset > 0 ? 'next' : 'center';
        tile.dataset.offset = offset; tile.dataset.entryKey = item.key;
        if (offset === 0) tile.setAttribute('role', 'img');
        tile.setAttribute('aria-label', `${item.name}, ${item.pack}${offset === 0 ? `, ${this.strings.selected}` : ''}`);
        if (item.image && this.safeImage(item.image)) {
          const img = document.createElement('img'); img.src = item.image; img.alt = `${item.name}, ${item.pack}`; img.draggable = false;
          img.width = item.imageWidth; img.height = item.imageHeight; img.decoding = 'async';
          if (item.imageSmall && this.safeImage(item.imageSmall)) { img.srcset = `${item.imageSmall} 300w, ${item.image} 600w`; img.sizes = '(min-width:750px) 190px, 210px'; }
          tile.append(img);
        } else { const label = document.createElement('span'); label.textContent = this.strings.imagePending; tile.append(label); }
        fragment.append(tile);
      }
      if (!count) { const empty = document.createElement('p'); empty.className = 'mbb__empty'; empty.textContent = this.strings.empty; fragment.append(empty); }
      wheel.packages.replaceChildren(fragment);
      wheel.root.querySelector('[data-selected-name]').textContent = entry?.name || this.strings.empty;
      wheel.root.querySelector('.mbb__selected').hidden = !entry;
      wheel.root.querySelector('[data-selected-pack]').textContent = entry?.pack || '';
      wheel.root.querySelector('[data-selected-price]').textContent = this.format(entry?.price);
      wheel.root.querySelector('[data-selected-availability]').textContent = entry && !entry.variantId ? this.strings.exactUnavailable : entry && !entry.available ? this.strings.unavailable : '';
      wheel.root.querySelector('[data-position-label]').textContent = count ? `${wheel.index + 1} / ${count}` : '0 / 0';
      wheel.root.querySelector('.mbb__navigation').hidden = count < 2;
    }
    safeImage(value) {
      try { const u = new URL(value, location.origin); return u.protocol === 'https:' && (u.hostname === 'cdn.shopify.com' || u.hostname === location.hostname) || u.origin === location.origin && u.pathname.startsWith('/review/'); } catch { return false; }
    }
    safeRecipe(value) {
      if (!value) return null;
      try { const u = new URL(value, location.origin); return ['https:', 'http:'].includes(u.protocol) && [location.hostname, 'www.mamami4u.com', 'mamami4u.com'].includes(u.hostname) && /^\/blogs\/[^/]+\/[^/]+\/?$/.test(u.pathname) ? u.href : null; } catch { return null; }
    }
    reserveGallery(group) {
      const columns = innerWidth < 951 ? 4 : 5;
      const maximum = Math.max(0, ...this.wheels[group].entries.map(e => e.ingredientImages?.length || 0));
      this.querySelector(`[data-detail="${group}"]`).style.setProperty('--mbb-gallery-height', `${Math.ceil(maximum / columns) * 104}px`);
    }
    renderDetails(group) {
      const entry = this.state[group], panel = this.querySelector(`[data-detail="${group}"]`);
      const fields = { description: 'description', ingredients: 'ingredients', note: 'ingredientNote', preparation: 'preparation', home: 'home', allergens: 'allergens', storage: 'storage' };
      const heading = panel.querySelector('[data-detail-name]'); heading.replaceChildren(document.createTextNode(entry?.name || this.strings.empty));
      if (entry) { const pack = document.createElement('span'); pack.textContent = entry.pack; heading.append(pack); }
      for (const [target, key] of Object.entries(fields)) { const field = panel.querySelector(`[data-detail-${target}]`); field.textContent = entry?.[key] || ''; field.hidden = !entry?.[key]; }
      const recipe = panel.querySelector('[data-detail-recipe]'), url = this.safeRecipe(entry?.recipeUrl);
      recipe.hidden = !url; recipe.removeAttribute('href'); recipe.textContent = entry?.recipeTitle || '';
      if (url) recipe.href = url;
      panel.querySelector('[data-recipe-extras]').hidden = !url;
      const gallery = panel.querySelector('[data-ingredient-gallery]'); gallery.replaceChildren();
      for (const ingredient of entry?.ingredientImages || []) {
        if (!ingredient.name || !this.safeImage(ingredient.image)) continue;
        const item = document.createElement('li'), img = document.createElement('img'), label = document.createElement('span');
        img.src = ingredient.image; img.alt = ''; img.width = ingredient.width || 180; img.height = ingredient.height || 180; img.loading = 'lazy'; img.decoding = 'async';
        label.textContent = ingredient.name; item.append(img, label); gallery.append(item);
      }
      this.reserveGallery(group);
    }
    renderSummary() {
      const updating = Object.values(this.wheels).some(w => w.busy || Math.abs(w.drag || 0) > 0);
      const sum = this.total(), locked = this.locked();
      this.state.request = updating ? 'updating' : 'idle';
      const quote = this.commerce.confirmedQuote;
      const confirmed = quote && quote.signature === this.compositionSignature() && quote.currency === this.dataset.currency && quote.original === sum;
      this.querySelector('[data-separate-price]').textContent = this.format(confirmed ? quote.total : sum);
      this.querySelector('[data-price-label]').textContent = confirmed ? this.strings.confirmedCartTotal : this.strings.breakfastTotal;
      const original = this.querySelector('[data-original-price]'), saving = this.querySelector('[data-confirmed-saving]');
      original.hidden = !confirmed; original.textContent = confirmed ? this.format(quote.original) : '';
      original.setAttribute('aria-label', confirmed ? `${this.strings.originalTotal}: ${this.format(quote.original)}` : '');
      saving.hidden = !confirmed; saving.textContent = confirmed ? `${this.strings.savings}: ${this.format(quote.saving)}` : '';
      this.querySelector('[data-start-over]').disabled = locked;
      const notice = this.querySelector('[data-persistence-status]');
      notice.hidden = !this.persistenceNotice; notice.textContent = this.persistenceNotice || '';
      this.renderComposition();
      for (const wheel of Object.values(this.wheels)) {
        for (const button of wheel.root.querySelectorAll('[data-direction], button[data-entry-key]')) button.disabled = locked;
        wheel.orbit.setAttribute('aria-disabled', String(locked));
        const entry = this.state[wheel.group], line = this.composition.get(entry?.variantId);
        const add = wheel.root.querySelector('[data-add-breakfast]');
        add.disabled = locked || wheel.busy || !!wheel.drag || !entry?.available || !entry.variantId || !validQuantity(entry, line ? line.quantity + 1 : quantityRule(entry).min);
        add.setAttribute('aria-label', `${this.strings.addBreakfast}: ${entry?.name || this.strings.empty}, ${entry?.pack || ''}`);
      }
      this.querySelector('[data-pair-status]').textContent = this.commerce.message || (!this.composition.size ? this.strings.emptyBreakfast : sum === null ? this.strings.quantityInvalid : '');
      this.querySelector('[data-cart-recheck]').hidden = !this.commerce.pending && (this.commerce.verified || !this.composition.size);
      this.querySelector('[data-cart-recheck]').disabled = this.commerce.busy || updating;
      this.querySelector('[data-cart-review]').hidden = !this.commerce.pending && !this.commerce.message && !this.commerce.storageFailed;
      for (const button of this.querySelectorAll('[data-purchase]')) {
        button.disabled = locked || this.commerce.storageFailed || updating || !this.commerce.verified || !this.composition.size || this.snapshot().some(entry => !entry.available || !entry.variantId) || sum === null || !!this.querySelector('[aria-invalid="true"]');
        button.textContent = this.commerce.busy ? this.strings.adding : this.commerce.verified ? this.strings.addToCart : this.strings.purchasePending;
        button.setAttribute('aria-busy', String(this.commerce.busy));
        button.setAttribute('aria-haspopup', 'dialog');
      }
      const contents = this.snapshot().map(entry => `${entry.quantity} × ${entry.name} ${entry.pack}`).join(' + ');
      this.querySelector('[data-composition-total]').textContent = this.format(confirmed ? quote.total : sum);
      this.querySelector('[data-composition-total-label]').textContent = confirmed ? this.strings.confirmedCartTotal : this.strings.breakfastTotal;
      const footerOriginal = this.querySelector('[data-original-composition-total]');
      footerOriginal.hidden = !confirmed; footerOriginal.textContent = confirmed ? this.format(quote.original) : '';
      footerOriginal.setAttribute('aria-label', confirmed ? `${this.strings.originalTotal}: ${this.format(quote.original)}` : '');
      this.querySelector('[data-closing-contents]').textContent = contents || this.strings.emptyBreakfast;
      this.querySelector('[data-closing-quantity]').textContent = this.strings.breakfastTotal + ': ' + this.format(sum);
    }
    liveContext() {
      const root = window.Shopify?.routes?.root;
      const url = new URL(root || '/', location.origin);
      const drawer = document.querySelector('cart-drawer');
      return location.protocol === 'https:' && ['www.mamami4u.com', 'mamami4u.com', 'mamami4u.myshopify.com'].includes(location.hostname) && window.Shopify?.shop === 'mamami4u.myshopify.com' && window.Shopify?.theme?.id && root && url.origin === location.origin && url.pathname.endsWith('/') && ['renderContents', 'getSectionsToRender', 'setActiveElement'].every(method => typeof drawer?.[method] === 'function') && typeof publish === 'function' && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !!PUB_SUB_EVENTS.cartUpdate;
    }
    endpoint(path) { return window.Shopify.routes.root + path; }
    async json(url, options = {}) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        const data = await response.json();
        return { response, data };
      } finally { clearTimeout(timer); }
    }
    async cartState() {
      const { response, data } = await this.json(this.endpoint('cart.js'));
      if (!response.ok || data.currency !== this.dataset.currency || !Array.isArray(data.items) || !data.items.every(item => Number.isSafeInteger(item.quantity) && item.quantity > 0 && validMoney(item.original_price) && validMoney(item.final_price) && validMoney(item.final_line_price))) throw Error(this.strings.verifyFailed);
      return data;
    }
    async liveSelections(snapshot, strict = true) {
      const url = new URL(location.href); url.searchParams.delete('sections'); url.searchParams.set('sections', this.dataset.sectionId);
      const section = await this.json(url.pathname + url.search);
      const html = section.data[this.dataset.sectionId];
      if (!section.response.ok || typeof html !== 'string') throw Error(this.strings.verifyFailed);
      const entries = [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-breakfast-entry]')].map(node => JSON.parse(node.textContent));
      return Promise.all(snapshot.map(async saved => {
        const entry = entries.find(item => item.key === saved.key && item.variantId === saved.variantId && item.productId === saved.productId);
        if (!entry || !entry.productHandle || !validMoney(entry.price)) {
          if (strict) throw Error(this.strings.unavailablePair);
          return { ...this.unavailableEntry(saved.variantId), quantity: saved.quantity };
        }
        if (!entry.available) {
          if (strict) throw Error(this.strings.unavailablePair);
          return { ...entry, quantity: saved.quantity };
        }
        const { response, data } = await this.json(this.endpoint(`products/${encodeURIComponent(entry.productHandle)}.js`));
        const variant = data.variants?.find(item => String(item.id) === entry.variantId);
        if (response.status === 404 || !variant) {
          if (strict) throw Error(this.strings.unavailablePair);
          return { ...entry, available: false, price: null, quantity: saved.quantity };
        }
        if (!response.ok || String(data.id) !== entry.productId || data.requires_selling_plan || variant.requires_selling_plan || !validMoney(variant.price) || variant.price !== entry.price) throw Error(this.strings.verifyFailed);
        if (!variant.available) {
          if (strict) throw Error(this.strings.unavailablePair);
          return { ...entry, available: false, quantity: saved.quantity };
        }
        return { ...entry, quantity: saved.quantity };
      }));
    }
    snapshot() {
      return [...this.composition].map(([id, { entry, quantity }]) => ({ ...entry, variantId: id, quantity }));
    }
    applyLive(entries) {
      for (const entry of entries) {
        const { quantity, ...data } = entry;
        for (const configured of this.entries.filter(item => item.variantId === entry.variantId)) Object.assign(configured, entry.removed ? { available: false, price: null } : data);
        const line = this.composition.get(entry.variantId);
        if (line) line.entry = data;
      }
      for (const wheel of Object.values(this.wheels)) {
        wheel.root.querySelector('[data-selected-price]').textContent = this.format(this.state[wheel.group]?.price);
        wheel.root.querySelector('[data-selected-availability]').textContent = this.state[wheel.group]?.available ? '' : this.strings.unavailable;
      }
    }
    async verifySelection() {
      const token = this.verificationToken = (this.verificationToken || 0) + 1;
      this.commerce.verified = false;
      if (!this.liveContext() || this.commerce.pending || this.commerce.journalCorrupt || !this.composition.size) { this.renderSummary(); return; }
      try {
        const [entries, cart] = await Promise.all([this.liveSelections(this.snapshot(), false), this.cartState()]);
        if (token !== this.verificationToken || !this.isConnected || this.commerce.pending) return;
        this.applyLive(entries);
        this.commerce.verified = entries.every(entry => entry.available && !entry.removed && validQuantity(entry, this.composition.get(entry.variantId)?.quantity)) && this.total() !== null;
        this.commerce.message = this.commerce.verified ? '' : this.strings.unavailablePair;
        this.confirmDiscount(cart);
      } catch (error) { if (token === this.verificationToken) this.commerce.message = error.message || this.strings.verifyFailed; }
      if (token === this.verificationToken) this.renderSummary();
    }
    compositionSignature() { return JSON.stringify(this.snapshot().map(entry => [entry.variantId, entry.quantity]).sort((a, b) => a[0].localeCompare(b[0]))); }
    confirmDiscount(cart) {
      this.commerce.confirmedQuote = null;
      const entries = this.snapshot(), original = this.total();
      if (!this.commerce.verified || !entries.length || cart.currency !== this.dataset.currency || !validMoney(original) || !validMoney(cart.original_total_price) || !validMoney(cart.total_price) || !validMoney(cart.total_discount) || cart.total_discount <= 0 || cart.original_total_price !== original || original - cart.total_price !== cart.total_discount || !cart.items.every(item => !item.selling_plan_allocation && !item.parent_relationship && entries.some(entry => entry.variantId === String(item.variant_id) && entry.price === item.original_price)) || !entries.every(entry => cart.items.filter(item => String(item.variant_id) === entry.variantId).reduce((sum, item) => sum + item.quantity, 0) === entry.quantity)) return;
      this.commerce.confirmedQuote = { signature: this.compositionSignature(), currency: cart.currency, original, total: cart.total_price, saving: cart.total_discount };
    }
    readPending() {
      const record = this.store.readJournal();
      this.commerce.pending = null; this.commerce.journalCorrupt = record.kind === 'corrupt';
      if (record.kind === 'pending') {
        this.commerce.pending = record.pending; this.commerce.operationId = record.operationId;
        this.commerce.message = this.strings.uncertain;
      } else if (record.kind === 'empty') {
        try {
          const legacy = JSON.parse(sessionStorage.getItem(this.storageKey));
          if (legacy) { this.commerce.pending = legacy; this.commerce.legacyPending = true; }
        } catch { this.commerce.journalCorrupt = true; }
      }
      if (this.commerce.journalCorrupt) this.commerce.message = this.strings.uncertain;
    }
    savePending() { this.store.saveJournal(this.commerce.pending, this.commerce.operationId); }
    clearPending() { this.store.clearJournal(this.commerce.operationId); this.commerce.pending = null; }
    async restorePending() {
      try {
        const pending = this.commerce.pending;
        if (this.commerce.legacyPending) {
          pending.tokenHash = await this.store.fingerprint({ token: pending.token }); delete pending.token;
          pending.scope = this.store.scope; this.commerce.operationId = crypto.randomUUID();
          this.savePending(); sessionStorage.removeItem(this.storageKey); this.commerce.legacyPending = false;
        }
        if (pending.scope === this.store.scope) {
          this.composition.clear();
          for (const saved of pending.entries) {
            if (this.composition.has(saved.variantId) || !Number.isSafeInteger(saved.quantity) || saved.quantity < 1) throw Error();
            const entry = this.entries.find(entry => entry.variantId === saved.variantId) || this.unavailableEntry(saved.variantId);
            this.composition.set(saved.variantId, { entry, quantity: saved.quantity });
          }
          this.saveDraft();
        }
        this.renderSummary();
      } catch { this.commerce.message = this.strings.uncertain; this.renderSummary(); return; }
      await this.recheck();
    }
    async purchase(button) {
      if (button.disabled || this.locked() || this.commerce.storageFailed || !this.commerce.verified || !this.liveContext() || Object.values(this.wheels).some(wheel => wheel.busy || wheel.drag || wheel.pointer)) return;
      const saved = this.snapshot();
      if (!saved.length || !saved.every(entry => validQuantity(entry, entry.quantity)) || new Set(saved.map(entry => entry.variantId)).size !== saved.length || this.querySelector('[aria-invalid="true"]')) return;
      clearTimeout(this.verificationTimer); this.verificationToken = (this.verificationToken || 0) + 1;
      this.commerce.busy = true; this.commerce.confirmedQuote = null; this.commerce.message = this.strings.adding; this.renderSummary();
      try {
        await this.store.withCartLock(async lock => {
          if (!lock) { this.commerce.message = this.strings.uncertain; return; }
          this.readPending();
          if (this.commerce.pending || this.commerce.journalCorrupt) { this.commerce.message = this.strings.uncertain; return; }
          await this.submitSnapshot(saved, button);
        });
      } catch { this.commerce.verified = false; this.persistenceNotice = this.strings.storageUnavailable; }
      finally { this.commerce.busy = false; this.renderSummary(); }
    }
    async submitSnapshot(saved, button) {
      let sent = false;
      try {
        const [entries, before] = await Promise.all([this.liveSelections(saved), this.cartState()]);
        for (const entry of entries) {
          if (!validQuantity(entry, entry.quantity)) throw Error(this.strings.verifyFailed);
          if (entry.price !== saved.find(item => item.variantId === entry.variantId).price) {
            this.applyLive(entries);
            throw Error(this.strings.priceChanged);
          }
        }
        const cart = document.querySelector('cart-drawer'), sections = cart.getSectionsToRender().map(section => section.id);
        cart.setActiveElement(button);
        this.commerce.operationId = crypto.randomUUID();
        this.commerce.pending = { entries, before: this.counts(before, entries), emptyBefore: before.item_count === 0, currency: before.currency, tokenHash: await this.store.fingerprint(before), scope: this.store.scope, definitive: false };
        this.savePending();
        sent = true;
        const result = await this.json(this.endpoint('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: JSON.stringify({ items: entries.map(entry => ({ id: entry.variantId, quantity: entry.quantity })), sections, sections_url: location.pathname }) });
        this.commerce.pending.definitive = result.response.status >= 400 && result.response.status < 500 && ![408, 429].includes(result.response.status);
        this.commerce.pending.error = typeof result.data.description === 'string' ? result.data.description : this.strings.addFailed;
        this.savePending();
        await this.reconcile(result.data.sections);
      } catch (error) {
        if (sent) {
          try { await this.reconcile(); } catch { this.commerce.message = this.strings.uncertain; }
        } else {
          if (this.commerce.pending) this.clearPending();
          this.commerce.message = error.message || this.strings.verifyFailed;
          this.commerce.verified = false;
        }
      }
    }
    counts(cart, entries) { return entries.map(entry => cart.items.filter(item => String(item.variant_id) === entry.variantId).reduce((sum, item) => sum + item.quantity, 0)); }
    async renderCart(cart, sections) {
      const drawer = document.querySelector('cart-drawer');
      const required = drawer.getSectionsToRender();
      const usable = value => required.every(section => typeof value?.[section.id] === 'string' && new DOMParser().parseFromString(value[section.id], 'text/html').querySelector(section.selector || '.shopify-section'));
      if (!usable(sections)) {
        const url = new URL(location.href); url.searchParams.set('sections', required.map(section => section.id).join(','));
        const result = await this.json(url.pathname + url.search);
        if (!result.response.ok || !usable(result.data)) throw Error(this.strings.drawerFailed);
        sections = result.data;
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'breakfast-builder' });
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      drawer.renderContents({ sections });
    }
    async reconcile(sections) {
      const pending = this.commerce.pending, cart = await this.cartState();
      if (!pending || cart.currency !== pending.currency) throw Error(this.strings.uncertain);
      const counts = this.counts(cart, pending.entries), delta = counts.map((count, index) => count - pending.before[index]);
      const complete = delta.every((quantity, index) => quantity === pending.entries[index].quantity);
      const unchanged = delta.every(quantity => quantity === 0);
      const firstCart = pending.emptyBefore && (complete && cart.item_count === pending.entries.reduce((sum, entry) => sum + entry.quantity, 0) && cart.items.every(item => pending.entries.some(entry => entry.variantId === String(item.variant_id))) || unchanged && pending.definitive && cart.item_count === 0);
      if (await this.store.fingerprint(cart) !== pending.tokenHash && !firstCart) throw Error(this.strings.uncertain);
      const prices = pending.entries.every(entry => cart.items.filter(item => String(item.variant_id) === entry.variantId).every(item => item.original_price === entry.price));
      await this.renderCart(cart, sections);
      if (complete) {
        this.clearPending(); this.commerce.message = prices ? this.strings.added : this.strings.serverPrice;
        this.commerce.verified = false;
        await this.verifySelection();
        this.commerce.message = prices ? this.strings.added : this.strings.serverPrice;
      } else if (unchanged && pending.definitive) {
        this.clearPending(); this.commerce.verified = false;
        await this.verifySelection(); this.commerce.message = pending.error;
      } else { this.commerce.message = delta.some(quantity => quantity !== 0) ? this.strings.partial : this.strings.uncertain; }
    }
    async recheck() {
      if (this.commerce.busy) return;
      if (!this.commerce.pending) { await this.verifySelection(); return; }
      if (!this.liveContext()) return;
      this.commerce.busy = true; this.commerce.message = this.strings.checking; this.renderSummary();
      try { await this.reconcile(); } catch { this.commerce.message = this.strings.uncertain; }
      finally { this.commerce.busy = false; this.renderSummary(); }
    }
  }
  customElements.define('mamami-breakfast-builder', BreakfastBuilder);
})();
